import { HsaFlowNodeType } from "@redotech/redo-conversion-hsa-common/hsa-flow";
import {
  HsaFlowGraph,
  HsaFlowGraphNode,
} from "@redotech/redo-conversion-hsa-common/hsa-flow-validate";
import {
  HsaQuestionnaireStepType,
  isHsaQuestionnaireStepType,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { assertNever } from "@redotech/util/type";
import { PartialFlowStep } from "../../steps";

/** The editor's in-progress steps, narrowed to HSA questionnaire members. */
export type HsaQuestionnaireFlowStep = Extract<
  PartialFlowStep,
  { type: HsaQuestionnaireStepType }
>;

export function isHsaQuestionnaireFlowStep(
  step: PartialFlowStep,
): step is HsaQuestionnaireFlowStep {
  return isHsaQuestionnaireStepType(step.type);
}

/**
 * Structural graph view of the in-progress editor steps, tolerant of
 * partially-configured steps, for dominator analysis and similar checks.
 * Callers holding loosely-typed step arrays narrow them first with
 * isHsaQuestionnaireFlowStep.
 */
export function hsaFlowGraphFromSteps(
  steps: readonly HsaQuestionnaireFlowStep[],
): HsaFlowGraph {
  const stepById = new Map<string, HsaQuestionnaireFlowStep>();
  for (const step of steps) {
    stepById.set(step.id, step);
  }

  const resolveEdge = (targetId: string | undefined): string | null => {
    if (targetId === undefined) {
      return null;
    }
    const target = stepById.get(targetId);
    if (!target || target.type === StepType.HSA_END) {
      return null;
    }
    return targetId;
  };

  const nodes: HsaFlowGraphNode[] = [];
  let rootId: string | null = null;

  for (const step of steps) {
    switch (step.type) {
      case StepType.TRIGGER:
        rootId = resolveEdge(step.nextId);
        break;
      case StepType.HSA_END:
        // Synthesized "Survey complete" ends; resolveEdge maps them to null.
        break;
      case StepType.HSA_QUESTION_GROUP:
        nodes.push({
          nodeId: step.id,
          nodeType: HsaFlowNodeType.GROUP,
          questionHandles: (step.questions ?? []).flatMap((question) =>
            question?.handle ? [question.handle] : [],
          ),
          nextId: resolveEdge(step.nextId),
        });
        break;
      case StepType.HSA_CONDITION:
        nodes.push({
          nodeId: step.id,
          nodeType: HsaFlowNodeType.CONDITION,
          matchingNextId: resolveEdge(step.matchingNextId),
          nonMatchingNextId: resolveEdge(step.nonMatchingNextId),
        });
        break;
      case StepType.HSA_KNOCKOUT:
        nodes.push({ nodeId: step.id, nodeType: HsaFlowNodeType.KNOCKOUT });
        break;
      case StepType.HSA_CONTENT:
        nodes.push({
          nodeId: step.id,
          nodeType: HsaFlowNodeType.CONTENT,
          nextId: resolveEdge(step.nextId),
        });
        break;
      default:
        assertNever(step);
    }
  }

  return { rootId, nodes };
}
